import React, { useLayoutEffect, useRef, useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { trackEvent } from "../util/analytics";
import { FiArrowLeft, FiArrowRight, FiKey, FiMonitor, FiSmartphone, FiTablet, FiUser } from "react-icons/fi";
import slideTo, { scrollToTop } from "./pageTransition";
import LocalizedContent, { useLocaleDocument } from "./localization";
import { portfolioCases, portfolioPreviewDocument } from "./CompanyStory";
import "./page-transition.css";
import "./company-story.css";
import "./portfolio-detail.css";

// 포트폴리오별 관련 서비스 링크
const relatedServiceLinks = {
  "funeral-operations": [
    { to: "/serp/features", label: "세로 상조 ERP 보기" },
    { to: "/subscription/cases", label: "구독형 시스템 개발 사례 보기" },
  ],
  "facility-operations": [
    { to: "/subscription/cases", label: "구독형 시스템 개발 사례 보기" },
  ],
  "member-communications": [
    { to: "/subscription/cases", label: "구독형 시스템 개발 사례 보기" },
  ],
  "subscription-platform": [
    { to: "/subscription/cases", label: "구독형 시스템 개발 사례 보기" },
  ],
};

const subDeviceOrder = ["pc", "pc", "tablet", "mobile"];
const subDeviceLabel = { pc: "PC", tablet: "TABLET", mobile: "MOBILE" };
const collageLayout = [
  ["pc", 1], ["mobile", 0], ["tablet", 2], ["pc", 3],
  ["tablet", 3], ["pc", 0], ["pc", 2], ["mobile", 2],
  ["mobile", 1], ["pc", 1], ["tablet", 0], ["pc", 3],
  ["pc", 4], ["tablet", 5], ["mobile", 2], ["pc", 0],
];

export default function PortfolioDetail({ language = "ko" }) {
  const navigate = useNavigate();
  const collageRef = useRef(null);
  const { portfolioId } = useParams();
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const index = Math.max(0, portfolioCases.findIndex((item) => item.id === portfolioId));
  const item = portfolioCases[index];
  const showcaseScreens = (item.showcaseImages || item.detailImages || []).filter(Boolean);
  const featureScreens = item.id === "funeral-operations"
    ? ["order", "event", "settlement", "field"].map((screen) => `/images/company-cases/funeral-showcase-${screen}-v2.png`)
    : (item.detailImages || []).filter(Boolean);
  const showcaseLabels = item.showcaseLabels || item.scope;
  const mobileScreens = item.mobileShowcaseImages || [item.mobileImage, item.mainMobileImage].filter(Boolean);
  const brandColors = item.id === "funeral-operations"
    ? [["HUMAN BLUE", "#0066B3"], ["NATURE GREEN", "#1FB25A"], ["HEART YELLOW", "#FFC20E"], ["SYSTEM NAVY", "#172235"]]
    : [["PRIMARY", item.accent], ["DEEP", "#172235"], ["SOFT GRAY", "#AAB4C2"], ["BLACK", "#080D16"]];
  // 한국어는 접두어 없이, 다른 언어는 /{언어} 접두어
  const pathPrefix = language === "ko" ? "" : `/${language}`;
  const move = (amount) => {
    const next = portfolioCases[(index + amount + portfolioCases.length) % portfolioCases.length];
    slideTo(navigate, `${pathPrefix}/company/portfolio/${next.id}`, undefined, amount > 0 ? "forward" : "back");
  };

  useLocaleDocument(language, "portfolio", { title: item.title, description: item.desc });
  useLayoutEffect(() => { scrollToTop(); }, [portfolioId]);

  // Track portfolio view event
  useEffect(() => {
    if (portfolioId) {
      trackEvent('portfolio_view', {
        portfolio_id: portfolioId,
        case_type: item?.type || '',
        page_language: language,
      });
    }
  }, [portfolioId, item?.type, language]);
  useLayoutEffect(() => {
    const stage = collageRef.current;
    if (!stage) return;
    const plane = stage.querySelector(".portfolio-collage-plane");
    const lead = stage.querySelector(".is-lead");
    const center = () => {
      plane.style.setProperty("--center-x", "0px");
      plane.style.setProperty("--center-y", "0px");
      const outer = stage.getBoundingClientRect();
      const inner = lead.getBoundingClientRect();
      plane.style.setProperty("--center-x", `${outer.left + outer.width / 2 - inner.left - inner.width / 2}px`);
      plane.style.setProperty("--center-y", `${outer.top + outer.height / 2 - inner.top - inner.height / 2}px`);
    };
    const observer = new ResizeObserver(center);
    observer.observe(stage);
    observer.observe(plane);
    center();
    return () => observer.disconnect();
  }, [portfolioId]);

  return <LocalizedContent lang={language}><div className={`story-page portfolio-detail-page ${item.backgroundImage ? "is-warm-project" : ""}`} style={{ "--case-accent": item.accent }}>
    <header className="story-topbar portfolio-detail-topbar">
      <button type="button" className="story-back" onClick={() => slideTo(navigate, `${pathPrefix}/company`, undefined, "back")}><FiArrowLeft/> 포트폴리오 목록</button>
      <div><button type="button" onClick={() => move(-1)} aria-label="이전 프로젝트"><FiArrowLeft/></button><span>{index + 1} / {portfolioCases.length}</span><button type="button" onClick={() => move(1)} aria-label="다음 프로젝트"><FiArrowRight/></button></div>
    </header>

    <main>
      <section className="portfolio-detail-hero" aria-labelledby="portfolio-project-title" style={{ "--detail-image": `url(${item.heroBackgroundImage || item.backgroundImage || item.image})` }}>
        <div className="portfolio-hero-copy">
          <span>{item.type} · PORTFOLIO</span>
          <h1 id="portfolio-project-title">{item.title}</h1>
          <p>{item.desc}</p>
          <ul>{item.scope.map((scope) => <li key={scope}>{scope}</li>)}</ul>
        </div>
        <div className="portfolio-hero-device">
          <div className="portfolio-hero-device-screen">
            <div className={`hero-login ${item.id === "funeral-operations" ? "is-butterfly-login" : ""}`}>
              <div className="hero-login-panel" aria-hidden="true">
                <header className="hl-brand"><i><b/><b/><b/><b/></i><span><strong>{item.type} 정보시스템</strong>{item.id === "funeral-operations" && <small>LIFE · WITH RESPECT</small>}</span></header>
                <div className="hl-body">
                  <strong className="hl-title">로그인</strong>
                  <p className="hl-greet">{item.id === "funeral-operations" ? "소중한 마지막을, 더 따뜻하고 존엄하게 함께 만들어갑니다." : "등록된 담당자 계정으로 접속해 주세요."}</p>
                  <label className="hl-field"><FiUser/><i/><span>아이디</span></label>
                  <label className="hl-field"><FiKey/><i/><span>비밀번호</span></label>
                  <div className="hl-row"><span><em/>아이디 저장</span><b>비밀번호 찾기</b></div>
                  <span className="hl-btn">로그인 <FiArrowRight/></span>
                  <div className="hl-note">
                    <p>사내망 또는 승인된 단말에서만 접속할 수 있습니다.</p>
                    <p>비밀번호 5회 오류 시 계정이 잠깁니다.</p>
                  </div>
                </div>
                <footer className="hl-foot">
                  <span>시스템 문의 02-0000-0000 · 평일 09:00–18:00</span>
                  <span>v2.4.1</span>
                </footer>
              </div>
              <div className="hero-login-visual" style={{ "--login-object": `url(${item.loginImage || item.image})` }}>
                <div className="hl-visual-caption" aria-hidden="true">
                  <span>{item.type} 정보시스템</span>
                  <strong>{item.id === "funeral-operations" ? <>소중한 마음을 잇는<br/>따뜻한 기술.</> : <>연결된 업무,<br/>더 간결한 일상.</>}</strong>
                </div>
              </div>
            </div>
          </div>
          <img className="portfolio-hero-device-frame" src="/images/company-cases/pc-device-mockup.png" alt="" aria-hidden="true"/>
        </div>
      </section>

      <div className="portfolio-detail-inner portfolio-editorial">
        <section className="portfolio-design-section portfolio-main-section">
          <div className="portfolio-design-heading"><span>01</span><div><small>MAIN DESIGN</small><h3>핵심 업무를 한 화면에서.</h3><p>{item.desc}</p></div></div>
          {item.desktopImage ? <div className={`portfolio-main-device ${item.id === "facility-operations" ? "is-facility" : ""}`}><div className="portfolio-main-laptop"><div className="portfolio-main-laptop-screen"><img className="portfolio-main-device-laptop" src={item.desktopImage} alt={`${item.title} 노트북 화면`}/></div><div className="portfolio-main-laptop-base" aria-hidden="true"/></div><img className="portfolio-main-device-phone" src={item.mainMobileImage || item.mobileImage} alt={`${item.title} 모바일 화면`}/><img className="portfolio-main-device-frame" src="/images/company-cases/laptop-phone-mockup.png" alt="" aria-hidden="true"/></div> : item.mockupImage ? <div className="portfolio-detail-3d-mockup"><img src={item.mockupImage} alt={`${item.title} 3D 디바이스 목업`}/><small>고객사 보안상 실제 운영 데이터는 제외하고 화면 구성을 재현했습니다.</small></div> : <div className="portfolio-dashboard-mock">
            <aside><b>SC</b>{item.scope.map((scope, scopeIndex) => <i className={scopeIndex === 0 ? "is-active" : ""} key={scope}>{scope.slice(0,2)}</i>)}</aside>
            <div className="portfolio-dashboard-body"><header><span><small>{item.type}</small><strong>{item.title}</strong></span><em>{item.status}</em></header><div className="portfolio-metric-row">{item.metrics.map(([label,value], metricIndex)=><article key={label}><span>0{metricIndex + 1}</span><small>{label}</small><strong>{value}</strong><i style={{height:`${48 + metricIndex * 16}%`}}/></article>)}</div><div className="portfolio-dashboard-lower"><div className="portfolio-chart"><span>업무 처리 현황</span>{[42,58,51,72,65,84,76,91].map((height,barIndex)=><i key={barIndex} style={{height:`${height}%`}}/>)}</div><div className="portfolio-task-list"><span>확인할 업무</span>{item.workflow.slice(0,4).map((step,stepIndex)=><p key={step}><i/><b>{step}</b><small>{stepIndex % 2 ? "진행 중" : "확인 필요"}</small></p>)}</div></div></div>
          </div>}
        </section>

        {featureScreens.length >= 2 && <section className="portfolio-feature-break" aria-label="주요 업무 화면 하이라이트">
          <div className="portfolio-feature-copy">
            <span>FOCUS ON THE DETAILS</span>
            <h3>복잡한 업무도,<br/>흐름은 간결하게.</h3>
            <p>{item.scope[0]}부터 {item.scope[2] || item.scope[1]}까지.<br/>업무에 필요한 정보를 가까이 모았습니다.</p>
          </div>
          {item.id === "funeral-operations" ? <figure className="portfolio-feature-art"><img src="/images/company-cases/funeral-mobile-feature-mockup.png" alt="상조 업무 앱의 로그인과 대시보드를 보여주는 모바일 목업" loading="lazy"/></figure> : <div className="portfolio-feature-stage">
            <figure className="portfolio-feature-screen"><img src={featureScreens[0]} alt={`${item.scope[0]} 화면 전체 구성`} loading="lazy"/><figcaption>{item.scope[0]}</figcaption></figure>
            <figure className="portfolio-feature-inset"><div><img src={featureScreens[2] || featureScreens[1]} alt={`${item.scope[2] || item.scope[1]} 화면 디자인`} loading="lazy"/></div><figcaption><span/>{item.scope[2] || item.scope[1]} · SCREEN DESIGN</figcaption></figure>
          </div>}
        </section>}

        {item.id === "facility-operations" && <section className="portfolio-facility-device-overview" aria-label="시설 유지관리 PC·태블릿·모바일 목업"><img src="/images/company-cases/facility-responsive-v2.png" alt="시설 유지관리 시스템의 노트북, 태블릿, 모바일 화면" loading="lazy"/></section>}

        <section className="portfolio-design-section portfolio-sub-section">
          <div className="portfolio-design-heading"><span>02</span><div><small>SUB DESIGN</small><h3>업무마다 필요한 화면을 연결합니다.</h3></div></div>
          <div className="portfolio-sub-grid">{item.scope.map((scope,scopeIndex)=>{
            const device = item.subDevices?.[scopeIndex] || subDeviceOrder[scopeIndex % subDeviceOrder.length];
            const shot = item.subImages?.[scopeIndex] || (device === "mobile" ? (item.mobileImage || item.detailImages?.[scopeIndex]) : item.detailImages?.[scopeIndex]);
            return <article className={`portfolio-sub-card is-${device} type-${scopeIndex + 1} ${item.subImages?.[scopeIndex] ? "has-original-screen" : ""}`} key={scope}>
              <header><small>0{scopeIndex + 1}</small><strong>{scope}</strong><em>{subDeviceLabel[device]}</em></header>
              <div className="portfolio-device-stage">
                <div className="portfolio-device-frame">
                  {device === "pc" && <span className="portfolio-device-chrome"><i/><i/><i/><b/></span>}
                  {device === "mobile" && <span className="portfolio-device-notch"/>}
                  <div className="portfolio-device-screen">{shot ? <img src={shot} alt={`${scope} 화면 구성`}/> : <div className="portfolio-device-skeleton"><span/><span/><span/><span/></div>}</div>
                </div>
              </div>
              <p>{item.captions?.[scopeIndex] || `${item.workflow[scopeIndex]}부터 다음 단계까지 같은 데이터로 이어집니다.`}</p>
            </article>;
          })}</div>
        </section>

        {item.id === "facility-operations" && <section className="portfolio-facility-field-overview" aria-label="시설 유지관리 태블릿과 모바일 목업"><img src="/images/company-cases/facility-tablet-mobile-v2.png" alt="현장 작업 보고를 보여주는 태블릿과 모바일 화면" loading="lazy"/></section>}

        <section className="portfolio-design-section portfolio-system-section">
          <div className="portfolio-design-heading"><span>03</span><div><small>DESIGN SYSTEM</small><h3>정보가 많은 업무 화면을 위한 기준.</h3></div></div>
          <div className="portfolio-brand-palette">{brandColors.map(([name,hex])=><div key={name} style={{"--brand-color":hex}}><strong>{name}</strong><span><small>HEX</small>{hex}</span></div>)}</div>
          <div className="portfolio-system-grid">
            <div className="portfolio-type-sample"><small>FONT SYSTEM</small><div className="portfolio-type-specimen"><div className="portfolio-type-row"><aside><b>KOR</b><strong>Pretendard</strong><p>가나다라마바사아<br/>업무 화면과 데이터<br/>Regular · Semibold · Bold</p></aside><div className="portfolio-type-glyphs is-korean"><span>가</span><span>나</span><span>다</span><span>라</span><span>마</span><span>바</span></div></div><div className={`portfolio-type-row ${item.id === "facility-operations" ? "uses-poppins" : ""}`}><aside><b>ENG · NUM</b><strong>{item.id === "facility-operations" ? "Poppins" : "Pretendard"}</strong><p>abcdefghijklmnopqrstuvwxyz<br/>ABCDEFGHIJKLMNOPQRSTUVWXYZ<br/>0123456789</p></aside><div className="portfolio-type-glyphs is-english"><span>a</span><span>A</span><span>B</span><span>C</span><span>D</span><span>1</span></div></div></div></div>
          </div>
        </section>

        {item.mockupImage && <section className="portfolio-responsive-art"><div className="portfolio-art-copy"><span>CONNECTED EXPERIENCE</span><h3>사무실에서 현장까지,<br/>하나로 이어지는 업무.</h3><p>PC의 관리 화면과 현장의 모바일 앱을 연결합니다.</p></div><img src={item.mockupImage} alt={`${item.title} 반응형 디바이스 디자인`}/></section>}

        <section className="portfolio-experience"><div className="portfolio-experience-heading"><span>04 · RESPONSIVE PREVIEW</span>{item.id !== "funeral-operations" && <><h3>{item.id === "facility-operations" ? "접수부터 현장 완료까지 직접 확인하세요." : "반응형 웹페이지를 경험하세요."}</h3><p>{item.id === "facility-operations" ? "A주유소 간판 조명 고장 접수부터 배정·현장 보고·정산까지 체험해 보세요. 기기를 바꿔도 진행 중인 작업이 이어집니다." : "기기 버튼을 선택하고 프리뷰 안을 직접 스크롤해 보세요."}</p></>}</div><div className={`portfolio-browser is-${previewDevice}`}><div className="portfolio-device-rail"><button type="button" className={previewDevice === "desktop" ? "is-active" : ""} onClick={() => setPreviewDevice("desktop")}><FiMonitor/><span>PC</span></button><button type="button" className={previewDevice === "tablet" ? "is-active" : ""} onClick={() => setPreviewDevice("tablet")}><FiTablet/><span>TABLET</span></button><button type="button" className={previewDevice === "mobile" ? "is-active" : ""} onClick={() => setPreviewDevice("mobile")}><FiSmartphone/><span>MOBILE</span></button></div><div className="portfolio-iframe-wrap"><iframe key={`${item.id}-${previewDevice}`} title={`${item.title} 미리보기`} src={item.previewPages?.[previewDevice]} srcDoc={item.previewPages ? undefined : portfolioPreviewDocument(item)}/></div></div></section>
        {showcaseScreens.length >= 2 && <section className="portfolio-screen-collage" aria-label="프로젝트 화면 모아보기">
          <div className="portfolio-collage-copy"><span>ONE SYSTEM, MANY POSSIBILITIES</span><h3>각자의 화면이,<br/>하나의 업무로 이어집니다.</h3></div>
          <div className="portfolio-collage-stage" ref={collageRef}>
            <div className="portfolio-collage-plane">
              {collageLayout.map(([device, shotIndex], position) => {
                const screenIndex = shotIndex % showcaseScreens.length;
                const mobileShot = mobileScreens[shotIndex % mobileScreens.length];
                const actualDevice = device === "mobile" && !mobileShot ? "pc" : device;
                const src = actualDevice === "mobile" ? mobileShot : showcaseScreens[screenIndex];
                const isLead = position === 5;
                return <figure className={`portfolio-collage-card is-${actualDevice} ${isLead ? "is-lead" : "is-surrounding"}`} key={position}>
                  <img src={src} alt={`${actualDevice === "mobile" ? (item.mobileShowcaseLabels?.[shotIndex % mobileScreens.length] || "현장 업무") : showcaseLabels[screenIndex] || item.title} ${subDeviceLabel[actualDevice]} 화면`} loading="lazy"/>
                </figure>;
              })}
            </div>
          </div>
        </section>}
      </div>

      {relatedServiceLinks[item.id] && language === "ko" && (
        <section className="portfolio-related-services">
          <span>RELATED SERVICES</span>
          <div className="portfolio-related-links">
            {relatedServiceLinks[item.id].map(({ to, label }) => (
              <Link key={to} to={`${pathPrefix}${to}`} className="portfolio-related-link">
                {label} <FiArrowRight/>
              </Link>
            ))}
          </div>
        </section>
      )}

      <nav className="portfolio-detail-more">
        <span>OTHER PROJECTS</span>
        <div className="portfolio-more-grid">
          {portfolioCases.filter((other) => other.id !== item.id).map((other) => (
            <button key={other.id} type="button" onClick={() => slideTo(navigate, `${pathPrefix}/company/portfolio/${other.id}`, undefined, "forward")}>
              <i style={{ backgroundImage: `url(${other.thumbnailImage || other.backgroundImage || other.image})` }}/>
              <small>{other.type}</small>
              <strong>{other.title}</strong>
              <em>상세 보기 <FiArrowRight/></em>
            </button>
          ))}
        </div>
      </nav>
    </main>
  </div></LocalizedContent>;
}
